/** Attach Authorization header for protected API paths and handle stale sessions. */

import { API_BASE } from "@/lib/utils";
import { isPublicEventHost } from "@/lib/canonical-host";
import {
  clearAuthSession,
  getAuthSession,
  getTokenExpiryMs,
  isTokenExpired,
  redirectToLogin,
  saveAuthSession,
} from "@/lib/auth-session";

export function getAuthHeaders() {
  const session = getAuthSession();
  if (!session?.token) return {};
  return { Authorization: `Bearer ${session.token}` };
}

function apiPathname(url) {
  return url.includes("://") ? new URL(url, window.location.origin).pathname : url;
}

function isPublicApiPath(url, method = "GET") {
  const path = apiPathname(url);
  const m = (method || "GET").toUpperCase();
  if (!path.startsWith("/api/")) return true;
  if (path === "/api/health" || path.startsWith("/api/health/")) return true;
  if (path === "/api/auth/login" || path.startsWith("/api/auth/login")) return true;
  if (path === "/api/auth/refresh" || path.startsWith("/api/auth/refresh")) return true;
  if (path.startsWith("/api/auth/identity")) return true;
  if (path === "/api/bootstrap-data") return true;
  if (path === "/api/public/landing" || path.startsWith("/api/public/landing")) return true;
  if (path === "/api/public/status" || path.startsWith("/api/public/status")) return true;
  if (path === "/api/public/inbound" || path.startsWith("/api/public/inbound")) return true;
  if (path === "/api/public/signup" || path.startsWith("/api/public/signup")) return true;
  if (path.startsWith("/api/public/")) return true;
  // Register checkout may read ticket-engine without a session.
  if (path.startsWith("/api/ticket-engine") && m === "GET") return true;
  if (path.startsWith("/api/events/") && path.includes("/public")) return true;
  // Public event pages may call bootstrap without a session; admin attaches Bearer when logged in.
  if (path === "/api/bootstrap-data") return false;
  if (path.startsWith("/api/state/")) return false;
  return false;
}

function shouldAttachAuth(url, method) {
  if (isPublicApiPath(url, method)) return false;
  const m = (method || "GET").toUpperCase();
  const path = apiPathname(url);
  if (url.includes("/api/platform")) return true;
  if (url.includes("/api/admin")) return true;
  if (url.includes("/api/auth/me")) return true;
  if (url.includes("/api/auth/identity") && !url.includes("/api/auth/identity/config") && !url.includes("/api/auth/identity/verify")) {
    return true;
  }
  if (path.startsWith("/api/ticket-engine")) return true;
  if (path.includes("/form-builder")) return true;
  if (path.startsWith("/api/state/")) return true;
  if (path === "/api/bootstrap-data") return true;
  if (path.startsWith("/api/admin/workspace/onboarding")) return true;
  // Admin console: tenant-scoped event APIs (attendees, check-in, reports, etc.)
  if (path.startsWith("/api/events/") && !path.includes("/public")) return true;
  return false;
}

function shouldHandleAuthFailure(url, method) {
  if (isPublicEventHost()) return false;
  if (isPublicApiPath(url, method)) return false;
  return shouldAttachAuth(url, method);
}

let refreshPromise = null;

async function refreshAuthSession(origFetch) {
  const session = getAuthSession();
  if (!session?.token) return false;

  const res = await origFetch(`${API_BASE}/api/auth/refresh`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${session.token}`,
      "content-type": "application/json",
    },
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok || !body?.token) return false;

  saveAuthSession({
    ...session,
    token: body.token,
    email: body.email || session.email,
    role: body.role || session.role,
    tenant: body.tenant || session.tenant,
  });
  return true;
}

async function ensureFreshSession(origFetch) {
  if (refreshPromise) return refreshPromise;
  refreshPromise = refreshAuthSession(origFetch).finally(() => {
    refreshPromise = null;
  });
  return refreshPromise;
}

async function handleUnauthorizedResponse(origFetch, url, method, input, init) {
  if (!shouldHandleAuthFailure(url, method)) return null;

  const refreshed = await ensureFreshSession(origFetch);
  if (!refreshed) {
    redirectToLogin("expired");
    return null;
  }

  const headers = new Headers(init?.headers || {});
  const extra = getAuthHeaders();
  if (extra.Authorization) headers.set("Authorization", extra.Authorization);
  const retryInit = { ...init, headers };
  if (typeof input === "object" && input instanceof Request) {
    return origFetch(new Request(input, retryInit));
  }
  return origFetch(input, retryInit);
}

export function attachAuthFetch() {
  const orig = window.fetch.bind(window);

  window.fetch = async (input, init = {}) => {
    let url = "";
    if (typeof input === "string") {
      url = input;
    } else if (input && typeof input === "object" && "url" in input) {
      url = String(input.url || "");
    }
    const method = init.method || (typeof input === "object" && input?.method) || "GET";
    const baseHeaders =
      init.headers || (typeof input === "object" && input instanceof Request ? input.headers : undefined);
    const headers = new Headers(baseHeaders || {});

    if (url && shouldAttachAuth(url, method)) {
      const session = getAuthSession();
      if (session?.token && isTokenExpired(session.token)) {
        await ensureFreshSession(orig);
      }
      const extra = getAuthHeaders();
      if (extra.Authorization && !headers.has("Authorization")) {
        headers.set("Authorization", extra.Authorization);
      }
    }

    const nextInit = { ...init, headers };
    const requestInput =
      typeof input === "object" && input instanceof Request ? new Request(input, nextInit) : input;
    const response = await orig(requestInput, typeof requestInput === "object" && requestInput instanceof Request ? undefined : nextInit);

    if (response.status !== 401 || !shouldHandleAuthFailure(url, method || "GET")) {
      return response;
    }

    let authCode = "";
    try {
      const body = await response.clone().json();
      authCode = String(body?.code || "");
    } catch {
      // ignore parse errors
    }

    if (authCode === "no_token") {
      redirectToLogin("expired");
      return response;
    }

    const retried = await handleUnauthorizedResponse(orig, url, method, input, nextInit);
    return retried || response;
  };
}

export function startSessionWatch() {
  if (typeof window === "undefined") return () => {};

  const orig = window.fetch.bind(window);
  const check = async () => {
    const session = getAuthSession();
    if (!session?.token) return;

    const expMs = getTokenExpiryMs(session.token);
    if (!expMs) return;

    const remaining = expMs - Date.now();
    const thirtyMinutes = 30 * 60 * 1000;

    if (isPublicEventHost()) {
      if (remaining <= 0) clearAuthSession();
      return;
    }

    if (remaining <= 0) {
      const refreshed = await refreshAuthSession(orig);
      if (!refreshed && !window.location.pathname.startsWith("/login")) {
        redirectToLogin("expired");
      }
      return;
    }

    if (remaining <= thirtyMinutes) {
      await refreshAuthSession(orig);
    }
  };

  const interval = window.setInterval(check, 5 * 60 * 1000);
  const onVisibility = () => {
    if (document.visibilityState === "visible") check();
  };
  document.addEventListener("visibilitychange", onVisibility);
  check();

  return () => {
    window.clearInterval(interval);
    document.removeEventListener("visibilitychange", onVisibility);
  };
}
